// Insurance A/R — one payer list for the whole feature (staging fix §7: the
// payer filter and the generated claims used different names, so the filter
// showed "2 of 19"). Every claim and write-off uses a canonical name from here;
// the slug is what links use (`/ar?payer=aetna,delta`).

export interface ArPayerDef {
  slug: string;
  name: string;
  short: string;
}

export const AR_PAYERS: ArPayerDef[] = [
  { slug: 'delta', name: 'Delta Dental of Texas', short: 'Delta' },
  { slug: 'cigna', name: 'Cigna', short: 'Cigna' },
  { slug: 'metlife', name: 'MetLife', short: 'MetLife' },
  { slug: 'aetna', name: 'Aetna', short: 'Aetna' },
  { slug: 'guardian', name: 'Guardian', short: 'Guardian' },
  { slug: 'concordia', name: 'United Concordia', short: 'United Concordia' },
  { slug: 'bcbs', name: 'BlueCross BlueShield of Texas', short: 'BCBS' },
  { slug: 'uhc', name: 'UnitedHealthcare', short: 'UHC' },
  { slug: 'sunlife', name: 'Sun Life', short: 'Sun Life' },
  { slug: 'humana', name: 'Humana', short: 'Humana' },
  { slug: 'principal', name: 'Principal', short: 'Principal' },
  { slug: 'ameritas', name: 'Ameritas', short: 'Ameritas' },
  { slug: 'anthem', name: 'Anthem Blue Cross', short: 'Anthem' },
  { slug: 'geha', name: 'GEHA', short: 'GEHA' },
];

/**
 * How many days each payer usually takes to pay (from the practice's own claims, Settings ›
 * Follow-up). A payer missing here has no usual pay time yet (Sun Life in the mock): its claims
 * are never "late by the payer's usual time"; only the practice's unpaid threshold or a state
 * deadline can make them late (D311).
 */
const USUAL_DAYS: Record<string, number> = {
  'Delta Dental of Texas': 14,
  Cigna: 14,
  MetLife: 14,
  Aetna: 21,
  Guardian: 14,
  'United Concordia': 28,
  'BlueCross BlueShield of Texas': 21,
  UnitedHealthcare: 21,
  Humana: 42,
  Principal: 14,
  Ameritas: 21,
  'Anthem Blue Cross': 14,
  GEHA: 21,
};

/** The payer's usual days to pay, or undefined when it has no usual pay time yet. */
export function usualDaysFor(name: string): number | undefined {
  return USUAL_DAYS[canonPayerName(name)];
}

/** 14 → "about 2 weeks". */
export function usuallyWords(days: number): string {
  const weeks = Math.max(1, Math.round(days / 7));
  return `about ${String(weeks)} ${weeks === 1 ? 'week' : 'weeks'}`;
}

/** Names the shared list generators use → the canonical A/R name. */
const ALIASES: Record<string, string> = {
  'Delta Dental': 'Delta Dental of Texas',
  'BCBS of Texas': 'BlueCross BlueShield of Texas',
};

export function canonPayerName(name: string): string {
  return ALIASES[name] ?? name;
}

export function payerShort(name: string): string {
  const n = canonPayerName(name);
  return AR_PAYERS.find((p) => p.name === n)?.short ?? n;
}

export function payerSlug(name: string): string {
  const n = canonPayerName(name);
  return AR_PAYERS.find((p) => p.name === n)?.slug ?? n.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** `?payer=aetna,delta` → the set of canonical payer names (unknown slugs are ignored). */
export function parsePayerParam(raw: string | null | undefined): Set<string> {
  const out = new Set<string>();
  if (!raw) return out;
  for (const part of raw.split(',')) {
    const t = part.trim().toLowerCase();
    if (t === '') continue;
    const hit = AR_PAYERS.find(
      (p) => p.slug === t || p.short.toLowerCase() === t || p.name.toLowerCase() === t,
    );
    if (hit) out.add(hit.name);
  }
  return out;
}

/** A link into Insurance A/R filtered to these payers (by name or slug). */
export function arPayerHref(payers: string[]): string {
  const slugs = payers.map(payerSlug);
  return slugs.length > 0 ? `/ar?payer=${slugs.join(',')}` : '/ar';
}

export const AR_LATE_HREF = '/ar?group=late';

/** `/ar?group=pays-patient` — opens A/R with "The payer pays the patient" expanded. */
export const AR_PAYS_PATIENT_HREF = '/ar?group=pays-patient';
